import { isValid, isWithinInterval, parseISO, subMonths } from 'date-fns';

import { CommitmentStatusEnum } from '@sola/schema/dotcom/supporters/commitments.ts';
import {
  type GraphQLCommitment,
  GraphQLCommitmentType,
  GraphQLSponsorshipChangeState,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';

export const SPONSORSHIP_COMMITMENT_TYPES: readonly GraphQLCommitmentType[] = [
  GraphQLCommitmentType.ChildSponsorship,
  GraphQLCommitmentType.Correspondence,
  GraphQLCommitmentType.LivingSponsorship,
];

export const COMMITMENT_PROGRAM_CHILD_SPONSORSHIP = 'CDSP';
export const COMMITMENT_SUBPROGRAM_SPONSORSHIP = 'Sponsor';
export const COMMITMENT_SUBPROGRAM_CORRESPONDENT = 'Correspondent';

/**
 * Prefix stamped on synthetic commitments the transaction cache queue injects
 * before upstream catches up. Kept here so producers and consumers agree.
 */
export const PENDING_COMMITMENT_ID_PREFIX = 'pending-commitment-';

export const isCommitmentActive = (
  commitment: Pick<GraphQLCommitment, 'status'>,
): boolean => commitment.status === CommitmentStatusEnum.Active;

export const isCommitmentNew = (
  commitment: Pick<GraphQLCommitment, 'dates'>,
): boolean => {
  const start = commitment.dates.start;

  if (!start) {
    return false;
  }

  const startDate = parseISO(start);

  if (!isValid(startDate)) {
    return false;
  }

  const now = new Date();
  return isWithinInterval(startDate, {
    start: subMonths(now, 1),
    end: now,
  });
};

/**
 * Accepted checkout commitment awaiting its upstream identifiers. Gated on the
 * synthetic id prefix so a genuine upstream Pending commitment is never treated
 * as one of our spoofed entries.
 */
export const isCommitmentPendingSubmission = (
  commitment: Pick<GraphQLCommitment, 'status' | 'internalId'>,
): boolean =>
  commitment.status === CommitmentStatusEnum.Pending &&
  commitment.internalId.startsWith(PENDING_COMMITMENT_ID_PREFIX);

export const isSponsorshipCommitment = (
  commitment: Pick<GraphQLCommitment, 'commitmentType'>,
): boolean => SPONSORSHIP_COMMITMENT_TYPES.includes(commitment.commitmentType);

/**
 * Is this commitment a sponsorship in a departure state, including the initial
 * 10-day transition window?
 */
export const isSponsorshipLeaving = (
  commitment: Pick<
    GraphQLCommitment,
    'commitmentType' | 'sponsorshipChangeState'
  >,
): boolean =>
  isSponsorshipCommitment(commitment) && !!commitment.sponsorshipChangeState;

/**
 * Is this commitment a sponsorship and in the initial 10 day leaving window
 */
export const isSponsorshipInTransition = (
  commitment?: Pick<GraphQLCommitment, 'sponsorshipChangeState'>,
): boolean =>
  commitment?.sponsorshipChangeState ===
  GraphQLSponsorshipChangeState.Transition;

export const isCommitmentCurrent = (
  commitment: Pick<GraphQLCommitment, 'status' | 'sponsorshipChangeState'>,
): boolean =>
  isCommitmentActive(commitment) || isSponsorshipInTransition(commitment);

export const isCommitmentCurrentRecurring = (
  commitment: Pick<
    GraphQLCommitment,
    'isRecurring' | 'status' | 'sponsorshipChangeState'
  >,
): boolean => isCommitmentCurrent(commitment) && commitment.isRecurring;

export const isCorrespondentCommitment = (
  commitment: Pick<GraphQLCommitment, 'commitmentType'>,
): boolean =>
  commitment.commitmentType === GraphQLCommitmentType.Correspondence;

export const areAllCommitmentsCorrespondent = (
  commitments: Pick<GraphQLCommitment, 'commitmentType'>[],
): boolean =>
  commitments.length > 0 && commitments.every(isCorrespondentCommitment);

export const isSponsorshipCommitmentGiftable = (
  commitment: Pick<GraphQLCommitment, 'canGiveGifts' | 'commitmentType'>,
): boolean => isSponsorshipCommitment(commitment) && commitment.canGiveGifts;

export const isSponsorshipCommitmentWritable = (
  commitment: Pick<GraphQLCommitment, 'canWrite' | 'commitmentType'>,
): boolean => isSponsorshipCommitment(commitment) && commitment.canWrite;

/**
 * Return "current" commitments - which could include sponsorships in transition
 */
export const filterCurrentCommitments = (
  commitments: GraphQLCommitment[],
): GraphQLCommitment[] => commitments.filter(isCommitmentCurrent);

/**
 * Return only sponsorship commitments
 */
export const filterSponsorshipCommitments = (
  commitments: GraphQLCommitment[],
): GraphQLCommitment[] => commitments.filter(isSponsorshipCommitment);

/**
 * Return only the "current" sponsorships - which could include sponsorships in transition
 */
export const filterCurrentSponsorshipCommitments = (
  commitments: GraphQLCommitment[],
): GraphQLCommitment[] =>
  filterSponsorshipCommitments(commitments).filter(isCommitmentCurrent);

/**
 * Return only the active sponsorships - excluding any departures
 */
export const filterActiveSponsorshipCommitments = (
  commitments: GraphQLCommitment[],
): GraphQLCommitment[] =>
  filterSponsorshipCommitments(commitments).filter(isCommitmentActive);
